import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { ReportAPIResponse } from '../api';
import { useHistory } from 'react-router-dom';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenRuler,
  faFileCirclePlus,
  faFileCircleCheck,
  faFileChartColumn,
} from '@fortawesome/pro-light-svg-icons';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { SuccessResponse, createNewReport } from '../NewReport/api';
import { useClient } from 'shared/apollo';
import { Program } from '../../Reports/NewReportDialog';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { getProgramNameUrl } from '../../ReportBuilder/Components/Budget/utils';

interface ReportYearProps {
  label: string;
  className?: string;
  program: Program;
  cardYear: number;
  report: ReportAPIResponse | undefined;
  isAdminUser: boolean;
  handleAdminModalOpen: (r: ReportAPIResponse) => unknown;
  handleAdminModalClose: () => unknown;
}

const StyledDiv = styled.div<{ isExists: boolean }>`
  cursor: pointer;
  text-align: center;
  padding: ${SPACERS.small};
  border-radius: 4px;
  ${({ isExists }) => (isExists ? `background-color: ${COLORS.whiteBase}` : '')}
  border: ${({ isExists }) =>
    isExists
      ? `1px solid ${COLORS.grayLight}`
      : `1px dotted ${COLORS.ctaPrimary}`}
  svg {
    width: 40px;
    height: 40px;
  }
  .report-heading {
    margin-top: ${SPACERS.tiny};
    margin-bottom: ${SPACERS.tiny};
  }
  .report-year {
    margin-bottom: ${SPACERS.tiny};
  }
  .report-status {
    border-radius: 4px;
    display: inline-block;
    padding: ${SPACERS.tiny} 9px;
    margin-bottom: ${SPACERS.medium};
    background-color: ${COLORS.grayLight};
  }
  .view-report {
    radius: 2px;
    display: inline-block;
    color: ${COLORS.whiteBase};
    background-color: ${COLORS.ctaPrimary};
    padding: ${SPACERS.tiny} ${SPACERS.small};
  }
`;

const getIcon = (report?: ReportAPIResponse) => {
  if (!report)
    return (
      <FontAwesomeIcon icon={faFileChartColumn} color={COLORS.ctaPrimary} />
    );
  switch (report.status) {
    case 'Draft':
    case 'Interim Updates Needed':
    case 'Final Updates Needed':
      return <FontAwesomeIcon icon={faPenRuler} color={COLORS.success} />;

    case 'Published':
    case 'Finalized':
      return (
        <FontAwesomeIcon icon={faFileCircleCheck} color={COLORS.success} />
      );

    default:
      return (
        <FontAwesomeIcon icon={faFileCirclePlus} color={COLORS.ctaPrimary} />
      );
  }
};

const ReportYear: React.FC<ReportYearProps> = ({
  label,
  report,
  cardYear,
  program,
  className,
  isAdminUser,
  handleAdminModalOpen,
}) => {
  const client = useClient();
  const { push } = useHistory();
  const [newReport, makeNewReportRequest, setNewReport] = useGraphQLRequest<
    SuccessResponse<'newReport'>
  >();

  useEffect(() => {
    if (newReport.type === 'REQUEST_SUCCESS') {
      push(
        `/npo/report-builder/${getProgramNameUrl(program.name)}/${
          newReport.data.newReport.id
        }`,
      );
    }
  }, [newReport]);

  const handleClick = useCallback(() => {
    if (!report) {
      client &&
        makeNewReportRequest(createNewReport(client, program, cardYear));
      return;
    }
    push(
      `/npo/report-builder/${getProgramNameUrl(program.name)}/${report.dbId}`,
    );
  }, [report, program]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!report || !isAdminUser) return;
      e.preventDefault();
      handleAdminModalOpen(report);
    },
    [report, isAdminUser, handleAdminModalOpen],
  );

  return (
    <StyledDiv
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      className={className}
      isExists={Boolean(report)}
    >
      {getIcon(report)}
      <TrueImpactText variant="heading-subheading" className="report-heading">
        {label}
      </TrueImpactText>
      <TrueImpactText variant="body-small" className="report-year">
        {cardYear}
      </TrueImpactText>
      {report && (
        <TrueImpactText
          variant="heading-label"
          color="blackBase"
          className="report-status"
        >
          {report.status}
        </TrueImpactText>
      )}
      <div>
        <TrueImpactText variant="body-small" className="view-report">
          {newReport.type === 'REQUEST_START' ? (
            <TrueImpactLoading
              singleLine={true}
              marginTop={4}
              marginBottom={4}
            />
          ) : report ? (
            'View Report'
          ) : (
            'Start Report'
          )}
        </TrueImpactText>
      </div>
    </StyledDiv>
  );
};

export default ReportYear;
